import React from "react";
import { useTranslation } from "react-i18next";
import { numberToLetter } from "../../utils/rateUtils";
import { formatDecimal } from "../../utils/format";

// État d'une prise pour l'affichage : libre, libre avec véhicule déjà branché,
// en recharge, ou indisponible.
export const connectorAvailability = (connector) => {
  if (connector?.hasTransaction) return "busy";
  if (connector?.status === "Preparing") return "preparing";
  if (connector?.status === "Available") return "available";
  return "unavailable";
};

const BORDER = {
  available: "border-qv-green-light",
  preparing: "border-qv-green-light",
  busy: "border-qv-brand",
  unavailable: "border-qv-red",
};

const STATUS_COLOR = {
  available: "text-qv-green",
  preparing: "text-qv-green",
  busy: "text-qv-brand font-semibold",
  unavailable: "text-[#980404]",
};

// Carte d'une prise (écrans 1a, 1c, 3a, 3b). Avec `onSelect`, la carte devient
// un bouton radio : contour violet et pastille pleine quand elle est choisie.
export default function ConnectorCard({ connector, price, selected = false, onSelect, disabled = false }) {
  const { t, i18n } = useTranslation();
  const availability = connectorAvailability(connector);
  const statusLabel = {
    available: t("available"),
    preparing: `${t("available")} (${t("vehicle_plugged")})`,
    busy: t("in_charge"),
    unavailable: t("unavailable"),
  }[availability];

  const selectable = Boolean(onSelect);
  const Tag = selectable ? "button" : "div";

  return (
    <Tag
      {...(selectable
        ? {
            type: "button",
            role: "radio",
            "aria-checked": selected,
            disabled,
            onClick: () => onSelect(connector),
          }
        : {})}
      className={`w-full flex items-center gap-3.5 rounded-xl border-l-8 p-4 text-left shadow-qv-card transition ${
        BORDER[availability]
      } ${selected ? "outline outline-2 outline-qv-brand bg-qv-brand-50" : "bg-white"} ${
        selectable && !disabled ? "cursor-pointer hover:bg-qv-brand-50" : ""
      } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
    >
      {selectable && (
        <span
          className={`h-[22px] w-[22px] shrink-0 rounded-full ${
            selected ? "border-[7px] border-qv-brand" : "border-2 border-[#A1A1AA]"
          }`}
          aria-hidden="true"
        />
      )}
      <span className="flex flex-col">
        <span className="font-semibold text-[#1F2937]">
          {t("connector", { number: numberToLetter(connector?.number) })}
        </span>
        <span className={`text-sm ${STATUS_COLOR[availability]}`}>{statusLabel}</span>
      </span>
      <span className="ml-auto shrink-0 whitespace-nowrap text-right font-semibold text-[#374151]">
        <span className="block text-lg">⚡ {Math.floor(connector?.powerMax || 0)} kW</span>
        {price && (
          <span className="block text-sm">⚡ {formatDecimal(price, i18n.language, 2)} €/kWh</span>
        )}
      </span>
    </Tag>
  );
}
